import {
  EmailReplyData,
  EmailTranscriptData,
  getEmailTranscript,
  getEmailTranscriptOneTime,
  GetShipmentAlerts,
  terminateAiEmail,
} from '@/apis/shipment';
import { CocEmail, ErrorLike } from '@/apis/types';
import { useToken } from '@/common/helper';
import { AI_EMAIL_TIMEOUT_MINUTES } from '@/common/constants';
import { Flex, message } from 'antd';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styles from './AIEmailExchange.module.css';
import DOMPurify from 'dompurify';
import { useSpin } from '../../../components/SpinContext';
import ToggleSwitch from '../../../components/ToggleSwitch';
import AIAlertDetails from './ui/AIAlertDetails';
import SectionTitle from './ui/SectionTitle';
import ResolutionWindowProgress from './ui/ResolutionWindowProgress';
import EmailComposer from './emailComposer/EmailComposer';
import { useEmailComposer } from './emailComposer/useEmailComposer';
import OutboxStatus from './emailComposer/OutboxStatus';
import PausedStrip from './emailComposer/PausedStrip';
import { useEmailReplyT } from './emailComposer/emailReplyStrings';
import { providerLogoFor } from '@/common/utils/providerLogo';
import { AiStar } from '@/assets/icons/icons';
import { postResumeEmailAi } from '@/apis/emailReply';
import type { EmailTranscriptItemMeta } from '@/apis/emailReplyContract';
import { PaperClipOutlined } from '@ant-design/icons';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { callEmailTimeFormatted } from './callEmailTimeUtils';
dayjs.extend(utc);
dayjs.extend(timezone);

type AvatarTone = 'white' | 'logo' | 'blue' | 'user';

const AVATAR_TONE_CLASS: Record<Exclude<AvatarTone, 'user'>, string> = {
  white: styles.avatarWhite,
  logo: styles.avatarLogo,
  blue: styles.avatarBlue,
};

/**
 * The 36 px circle of an author. The fixed colours are classes; only the
 * signed-in user's badge colour comes from their token, at run time, so that
 * one goes through the component's `style`.
 */
const AvatarCircle: React.FC<{
  tone: AvatarTone;
  userColor: string;
  children: React.ReactNode;
}> = ({ tone, userColor, children }) =>
  tone === 'user' ? (
    <Flex
      className={`${styles.avatar} ${styles.avatarUser}`}
      style={{ background: userColor }}
    >
      {children}
    </Flex>
  ) : (
    <div className={`${styles.avatar} ${AVATAR_TONE_CLASS[tone]}`}>
      {children}
    </div>
  );

/**
 * Keys for a list that may repeat a value: the value, plus a counter for the
 * second and later copies. Stable while the list does not change order.
 */
function uniqueKeys<T extends string>(
  values: readonly T[]
): Array<{ key: string; value: T }> {
  const seen = new Map<string, number>();
  return values.map((value) => {
    const count = seen.get(value) ?? 0;
    seen.set(value, count + 1);
    return { key: count === 0 ? value : `${value}#${count}`, value };
  });
}

function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0][0] ?? '?').toUpperCase();
  return (
    (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
  ).toUpperCase();
}

/**
 * The thread id goes on the request only when the user clicked Reply on a
 * specific entry; every other request is exactly what it was before.
 */
const fetchEmailTranscript = (
  token: string,
  alertId: string,
  isChecked: boolean,
  threadId?: string | null
) =>
  threadId
    ? getEmailTranscript(token, alertId, isChecked, threadId)
    : getEmailTranscript(token, alertId, isChecked);

/** The id the transcript is fetched by: the event for a one-time message, else the alert. */
const transcriptTargetId = (
  notificationType: string | undefined,
  eventId: string | undefined,
  id: string | undefined
): string | undefined =>
  notificationType === 'one_time_notification' ? eventId : id;

interface Alert {
  id?: string;
  notification_type?: string;
  ai_events?: Array<{ id: string }>;
  eventId?: string;
  ai_status?: string;
  ai_start_time?: string | null;
  /** Reply-to-AI-emails: the entry Reply / View was clicked on. */
  email?: CocEmail;
  open_composer?: boolean;
}

interface AIEmailExchangeProps {
  alert: Alert;
  token: string;
  setAlertId: (id: string | null) => void;
  unauthorize: (error: ErrorLike) => void;
  onClose: (close: boolean) => void;
  onDismiss: () => void;
  onResolve: () => void;
  onOpenAgent?: (agentId: string, readOnly: boolean) => void;
  key?: number;
  shipmentId: string;
  showHeaderButtons?: boolean;
}

/**
 * AIEmailExchange Component
 *
 * Displays AI email transcript data for shipment alerts including:
 * - Alert details with status, sentiment, and summary
 * - Email thread conversation with timestamps
 * - Language toggle for translations
 * - Next step recommendations based on status
 */
const AiEmailExchange: React.FC<AIEmailExchangeProps> = ({
  alert,
  token,
  unauthorize,
  onClose,
  setAlertId,
  onDismiss,
  onResolve,
  key,
  shipmentId,
  showHeaderButtons = true,
}) => {
  const { decodedUserToken } = useToken();
  const isTimeZone = decodedUserToken()?.time_zone_enable;
  const dateFormat = decodedUserToken()?.date_format;
  const { showSpin, hideSpin } = useSpin();
  const [transcriptData, setTranscriptData] =
    useState<EmailTranscriptData | null>(null);
  const [isChecked, setIsChecked] = useState(false);
  // Guard to prevent multiple simultaneous termination calls
  const isTerminatingRef = useRef<boolean>(false);
  // Track alert status locally - updated from termination response
  const [localAlertStatus, setLocalAlertStatus] = useState<string | undefined>(
    alert?.ai_status
  );

  const preferredLang = useMemo(() => {
    const lang = transcriptData?.company_user_preferred_language || 'English';
    return lang.charAt(0).toUpperCase() + lang.slice(1);
  }, [transcriptData]);

  useEffect(() => {
    const targetId = transcriptTargetId(
      alert?.notification_type,
      alert?.eventId,
      alert?.id
    );
    if (!targetId) return;
    setTranscriptData(null);
    showSpin();
    if (alert?.notification_type === 'one_time_notification') {
      getEmailTranscriptOneTime(token, targetId, isChecked, shipmentId)
        .then((res) => {
          const data = res?.data?.data || null;
          if (data?.is_success) {
            setTranscriptData(data.data);
            // Update localAlertStatus from transcript data (current database status)
            if (data.data?.ai_status) {
              setLocalAlertStatus(data.data.ai_status);
            }
          } else {
            message.error(data?.message || 'Unable to load transcript.');
          }
        })
        .catch((error: unknown) => {
          setTranscriptData(null);
          unauthorize?.(error as ErrorLike);
        })
        .finally(() => hideSpin());
    } else {
      fetchEmailTranscript(token, targetId, isChecked, alert.email?.thread_id)
        .then((res) => {
          const data = res?.data || null;
          if (data?.data?.is_success) {
            setTranscriptData(data.data.data);
            setAlertId(data.data.data?.alert_id ?? null);
            // Update localAlertStatus from transcript data (current database status)
            if (data.data.data?.ai_status) {
              setLocalAlertStatus(data.data.data.ai_status);
            }
          } else {
            message.error(data?.data?.message || 'Unable to load transcript.');
          }
        })
        .catch((error: unknown) => {
          setTranscriptData(null);
          unauthorize?.(error as ErrorLike);
        })
        .finally(() => hideSpin());
    }
  }, [
    alert,
    token,
    isChecked,
    showSpin,
    unauthorize,
    hideSpin,
    setAlertId,
    key,
    shipmentId,
  ]);

  // Sync local alert status with prop when alert changes
  useEffect(() => {
    setLocalAlertStatus(alert?.ai_status);
  }, [alert?.ai_status, alert?.id]);

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsChecked(e.target.checked);
  };

  const shouldShowNextStep = transcriptData?.show_buttons;

  const [showFull, setShowFull] = useState(false);
  const userBadgeColor = decodedUserToken()?.badge_color ?? '#4B6BFB';

  // Reload the thread without clearing it first, so nothing the user is typing
  // (kept in the composer hook, outside transcriptData) is ever lost.
  const reloadTranscript = useCallback(async () => {
    if (!alert?.id || alert?.notification_type === 'one_time_notification') {
      return;
    }
    const res = await fetchEmailTranscript(
      token,
      alert.id,
      isChecked,
      alert.email?.thread_id
    );
    const data = res?.data || null;
    if (data?.data?.is_success) {
      setTranscriptData(data.data.data);
    }
  }, [
    alert?.id,
    alert?.notification_type,
    alert?.email?.thread_id,
    token,
    isChecked,
  ]);

  const composer = useEmailComposer({
    token,
    alertId: transcriptData?.alert_id,
    // One-time notification threads have no composer in V1.
    native:
      alert?.notification_type === 'one_time_notification'
        ? undefined
        : transcriptData?.native,
    openComposer: Boolean(alert?.open_composer),
    reloadTranscript,
  });

  const { t: tEmail } = useEmailReplyT();
  const [resuming, setResuming] = useState(false);
  const replyThread = transcriptData?.native?.reply_target_thread ?? null;
  // Only an AI (Case A) thread pauses: after a human reply the AI stops
  // answering by itself until someone resumes it.
  const showPausedStrip =
    alert?.notification_type !== 'one_time_notification' &&
    Boolean(replyThread?.paused) &&
    replyThread?.case === 'A';

  const handleResumeAi = useCallback(async () => {
    const shipmentAlertId = transcriptData?.alert_id;
    if (!shipmentAlertId) return;
    setResuming(true);
    try {
      await postResumeEmailAi(token, shipmentAlertId);
      await reloadTranscript();
    } catch {
      message.error(tEmail('resumeFailed'));
    } finally {
      setResuming(false);
    }
  }, [transcriptData?.alert_id, token, reloadTranscript, tEmail]);

  const currentUserName = decodedUserToken()?.name ?? '';
  const currentUserEmail = decodedUserToken()?.email ?? '';

  const allMessages = useMemo(() => {
    const transcript = (transcriptData?.transcript ?? []).map(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (msg: any) => ({ ...msg, _type: 'transcript' as const })
    );
    const persistedReplies =
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((transcriptData as any)?.email_replies ?? []).map(
        (reply: EmailReplyData) => ({ ...reply, _type: 'reply' as const })
      );
    const sorted = [...transcript, ...persistedReplies].sort((a, b) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ta = (a as any).timestamp ?? (a as any).sent_at;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tb = (b as any).timestamp ?? (b as any).sent_at;
      return new Date(ta ?? 0).getTime() - new Date(tb ?? 0).getTime();
    });

    // Replies the user has just sent show at once ("Sending..." -> "Just now")
    // until the reload brings the saved copy.
    const savedIds = new Set(
      persistedReplies.map((reply: EmailReplyData) => reply.id)
    );
    const outboxMessages = composer.outbox
      .filter(
        (item) =>
          !(item.status === 'sent' && item.reply && savedIds.has(item.reply.id))
      )
      .map((item) => ({
        id: item.localId,
        body: item.body,
        sent_by: item.reply?.sent_by ?? {
          id: '',
          name: currentUserName,
          email: currentUserEmail,
        },
        sent_at: item.reply?.sent_at ?? new Date().toISOString(),
        to: item.reply?.to,
        _type: 'reply' as const,
        _outbox: item,
      }));
    return [...sorted, ...outboxMessages];
  }, [transcriptData, composer.outbox, currentUserName, currentUserEmail]);

  // Handle manual cancellation of AI email
  const handleCancelAiEmail = useCallback(async () => {
    // Guard: prevent multiple calls - check and set immediately
    if (isTerminatingRef.current) {
      return;
    }
    isTerminatingRef.current = true;

    try {
      const shipmentAlertId = transcriptData?.alert_id;

      if (!shipmentAlertId) {
        message.error(
          'Unable to cancel AI email: ShipmentAlert ID not available. Please refresh the page.'
        );
        return;
      }

      if (alert?.ai_status !== 'ai_email_in_progress') {
        return;
      }

      showSpin();
      const payload = {
        alert_id: shipmentAlertId,
        reason: 'User requested cancellation',
      };

      const response = await terminateAiEmail(token, payload);

      if (response?.data?.data?.is_success !== false) {
        // Update local alert status from termination response
        // If termination succeeded, status should be 'ai_email_completed'
        const updatedAiStatus = response?.data?.data?.data?.ai_status;
        if (updatedAiStatus) {
          setLocalAlertStatus(updatedAiStatus);
        } else {
          // Fallback: if response doesn't include status, assume it's completed
          setLocalAlertStatus('ai_email_completed');
        }
        message.success('AI email conversation has been canceled.');

        // Refresh transcript data to show the cancellation message
        const targetId = transcriptTargetId(
          alert?.notification_type,
          alert?.eventId,
          alert?.id
        );
        if (targetId && alert?.notification_type === 'one_time_notification') {
          const res = await getEmailTranscriptOneTime(
            token,
            targetId,
            isChecked,
            shipmentId
          );
          const data = res?.data?.data || null;
          if (data?.is_success) {
            setTranscriptData(data.data);
          }
        } else if (targetId) {
          const res = await fetchEmailTranscript(
            token,
            targetId,
            isChecked,
            alert.email?.thread_id
          );
          const data = res?.data || null;
          if (data?.data?.is_success) {
            setTranscriptData(data.data.data);
            setAlertId(data.data.data?.alert_id ?? null);
          }
        }

        // Refresh alerts to update alert card status immediately
        try {
          await GetShipmentAlerts(token, shipmentId);
        } catch (error) {
          console.warn(
            '[handleCancelAiEmail] Failed to refresh alerts:',
            error
          );
          // Don't show error to user - alerts will update on next poll
        }
      } else {
        message.error(
          response?.data?.data?.message || 'Failed to cancel AI email'
        );
      }
    } catch (error: unknown) {
      // Guard is still set - prevents any re-entry even on error
      console.error('[handleCancelAiEmail] Error:', error);
      const errorResponse = error as {
        response?: { status?: number; data?: { message?: string } };
      };
      const status = errorResponse?.response?.status;
      const errorMessage = errorResponse?.response?.data?.message;

      message.error(errorMessage || 'Failed to cancel AI email');

      // Only call unauthorize for auth errors - this might navigate away
      // But guard is still set, so even if component re-renders, function won't run again
      if (status === 401 || status === 403) {
        unauthorize?.(error as ErrorLike);
      }
    } finally {
      // Reset guard ONLY after everything is done - this prevents infinite loops
      isTerminatingRef.current = false;
      hideSpin();
    }
  }, [
    alert?.id,
    alert?.ai_status,
    alert?.notification_type,
    alert?.eventId,
    alert.email?.thread_id,
    transcriptData?.alert_id,
    token,
    isChecked,
    shipmentId,
    showSpin,
    hideSpin,
    unauthorize,
    setAlertId,
    setTranscriptData,
  ]);

  // Handle timer expiration - automatically terminate when timeout expires (60 minutes)
  const handleTimerExpire = useCallback(async () => {
    // Guard: prevent multiple calls - check and set immediately
    if (isTerminatingRef.current) {
      return;
    }
    isTerminatingRef.current = true;

    try {
      // Must use alert_id from transcript data - this is the ShipmentAlert ID
      const shipmentAlertId = transcriptData?.alert_id;

      if (!shipmentAlertId || alert?.ai_status !== 'ai_email_in_progress') {
        // If we don't have the ShipmentAlert ID, silently return
        // The backend timeout task will handle termination
        return;
      }

      showSpin();
      const payload = {
        alert_id: shipmentAlertId,
        reason: 'AI email resolution window expired',
      };

      const response = await terminateAiEmail(token, payload);

      if (response?.data?.data?.is_success !== false) {
        // Update local alert status from termination response
        // If termination succeeded, status should be 'ai_email_completed'
        const updatedAiStatus = response?.data?.data?.data?.ai_status;
        if (updatedAiStatus) {
          setLocalAlertStatus(updatedAiStatus);
        } else {
          // Fallback: if response doesn't include status, assume it's completed
          setLocalAlertStatus('ai_email_completed');
        }
        message.info(
          'AI email timer expired - conversation has been terminated.'
        );

        // Refresh transcript data to show the timeout message
        const targetId = transcriptTargetId(
          alert?.notification_type,
          alert?.eventId,
          alert?.id
        );
        if (targetId && alert?.notification_type === 'one_time_notification') {
          const res = await getEmailTranscriptOneTime(
            token,
            targetId,
            isChecked,
            shipmentId
          );
          const data = res?.data?.data || null;
          if (data?.is_success) {
            setTranscriptData(data.data);
          }
        } else if (targetId) {
          const res = await fetchEmailTranscript(
            token,
            targetId,
            isChecked,
            alert.email?.thread_id
          );
          const data = res?.data || null;
          if (data?.data?.is_success) {
            setTranscriptData(data.data.data);
            setAlertId(data.data.data?.alert_id ?? null);
          }
        }

        // Refresh alerts to update alert card status immediately
        try {
          await GetShipmentAlerts(token, shipmentId);
        } catch (error) {
          console.warn('[handleTimerExpire] Failed to refresh alerts:', error);
          // Don't show error to user - alerts will update on next poll
        }
      } else {
        // If termination fails, log but don't show error (backend task will handle it)
        console.warn(
          '[handleTimerExpire] Failed to terminate expired AI email:',
          {
            message: response?.data?.data?.message,
            response: response?.data?.data,
          }
        );
      }
    } catch (error: unknown) {
      // Guard is still set - prevents any re-entry even on error/timeout
      console.error('[handleTimerExpire] Error:', error);
      // Don't show error to user - backend task will handle timeout
      // Don't call unauthorize - this is a background operation
    } finally {
      // Reset guard ONLY after everything is done - this prevents infinite loops
      isTerminatingRef.current = false;
      hideSpin();
    }
  }, [
    alert?.id,
    alert?.ai_status,
    alert?.notification_type,
    alert?.eventId,
    alert.email?.thread_id,
    transcriptData?.alert_id,
    token,
    isChecked,
    shipmentId,
    showSpin,
    hideSpin,
    setAlertId,
    setTranscriptData,
  ]);

  // A transcript message is keyed by its id (or what identifies it when an old
  // message has none), a saved reply by its id.
  const messageKeys = uniqueKeys(
    allMessages.map((entry) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const msg = entry as any;
      return entry._type === 'transcript'
        ? `t-${msg.meta?.message_id ?? `${msg.timestamp}|${msg.direction}|${msg.from ?? msg.sender ?? ''}`}`
        : `r-${msg.id}`;
    })
  ).map(({ key }) => key);

  return (
    <Fragment>
      <AIAlertDetails
        subtitle='AI email'
        alert={alert}
        isCritical={transcriptData?.is_critical ?? false}
        title={
          alert?.notification_type === 'one_time_notification'
            ? 'One-time message'
            : (transcriptData?.title ?? '')
        }
        alertTitle={
          alert?.notification_type === 'one_time_notification'
            ? 'One-time message'
            : (transcriptData?.alert_title ?? '')
        }
        pillLabel={transcriptData?.status_info?.description}
        triggeredBy={
          transcriptData?.triggered_by_user_name || transcriptData?.triggered_by
        }
        recipient={transcriptData?.recipients}
        message={transcriptData?.message_content}
        sentimentLabel={
          transcriptData?.sentiment_analysis?.type ?? 'awaiting_reply'
        }
        summary={transcriptData?.summary || 'No summary provided.'}
        summaryLabel={tEmail('aiSummary')}
        nextStep={
          transcriptData?.next_step ||
          (transcriptData?.contact_info?.phone
            ? `Call ${transcriptData?.contact_info?.name} at ${transcriptData?.contact_info?.phone} to resolve`
            : `Contact not provided for ${transcriptData?.contact_info?.name || 'recipient'}`)
        }
        showNextStep={shouldShowNextStep}
        onDismiss={onDismiss}
        onResolve={onResolve}
        onClose={onClose}
        showHeaderButtons={showHeaderButtons}
        emailLayout
        resolutionWindow={
          localAlertStatus === 'ai_email_in_progress' ? (
            <ResolutionWindowProgress
              aiStartTime={alert?.ai_start_time}
              timeoutMinutes={AI_EMAIL_TIMEOUT_MINUTES}
              showCancelButton={true}
              onCancel={handleCancelAiEmail}
              onExpire={handleTimerExpire}
            />
          ) : undefined
        }
      />

      <SectionTitle
        right={
          <Flex align='center' gap={8} className={styles.sectionActions}>
            <ToggleSwitch
              label=''
              labelStyle={styles.toggle}
              style={{ width: 40 }}
              checked={isChecked}
              onChange={handleToggle}
              alignLeft
            />
            <span className={styles.preferredLang}>
              Show in {preferredLang}
            </span>
          </Flex>
        }
      >
        <span className='font-semibold text-base'>
          {tEmail('emailTranscript')}
        </span>
      </SectionTitle>

      {isChecked && (
        <div className={`ai-translation-note ${styles.translationNote}`}>
          Message is a translation to recipient’s preferred language of{' '}
          {preferredLang}.
        </div>
      )}

      {/* Email Thread Timeline */}
      <div
        className={`${styles.timeline} ${composer.visible ? styles.timelineWithComposer : ''}`}
      >
        {allMessages.map((entry, index) => {
          const showLineAbove = index > 0;
          const isLastMessage = index === allMessages.length - 1;
          // The reply box follows the last message, so the line goes on.
          const showLineBelow = !isLastMessage || composer.visible;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const msg = entry as any;

          let avatarTone: AvatarTone;
          let avatarContent: React.ReactNode;

          // The author kind, only when the response carries `native`.
          const authorType = transcriptData?.native
            ? (msg.meta?.author?.type as string | undefined)
            : undefined;

          // Reply-to-emails fields of this message; absent for old messages.
          const meta: EmailTranscriptItemMeta | undefined =
            entry._type === 'transcript' ? msg.meta : undefined;
          const courierLogo = providerLogoFor(meta?.author?.courier);

          // Figma: an AI-sent message is a white circle with a thin border and a
          // black sparkle; a received courier email shows the courier's logo
          // (initials when none is known); a teammate keeps a coloured disc with
          // their initials.
          if (
            entry._type === 'transcript' &&
            authorType !== 'USER' &&
            (authorType === 'AI' || msg.direction?.toLowerCase() === 'outbound')
          ) {
            avatarTone = 'white';
            avatarContent = (
              <span
                role='img'
                aria-label={tEmail('aiEmailAgent')}
                data-testid='ai-avatar'
                className={styles.aiAvatarIcon}
              >
                <AiStar size={16} color='currentColor' />
              </span>
            );
          } else if (entry._type === 'transcript' && authorType === 'USER') {
            avatarTone = 'user';
            avatarContent = (
              <span className={`${styles.initials} ${styles.initialsLarge}`}>
                {getInitials(msg.meta?.author?.name || '')}
              </span>
            );
          } else if (entry._type === 'transcript' && courierLogo) {
            avatarTone = 'logo';
            avatarContent = (
              <img
                className={styles.courierLogo}
                src={courierLogo}
                alt={meta?.author?.courier ?? ''}
              />
            );
          } else if (entry._type === 'transcript') {
            avatarTone = 'blue';
            // The same name the header shows, so the two cannot disagree.
            avatarContent = (
              <span className={styles.initials}>
                {getInitials(
                  meta?.author?.name || msg.from_name || msg.sender || ''
                )}
              </span>
            );
          } else {
            avatarTone = 'user';
            avatarContent = (
              <span className={styles.initials}>
                {getInitials(msg.sent_by?.name || '')}
              </span>
            );
          }

          const senderName =
            entry._type === 'transcript'
              ? meta?.author?.name || msg.from_name || msg.sender || ''
              : msg.sent_by?.name || '';
          const senderEmail =
            entry._type === 'transcript'
              ? meta?.author?.account_email || msg.from || ''
              : msg.sent_by?.email || '';
          const timestamp =
            entry._type === 'transcript' ? msg.timestamp : msg.sent_at;
          const isInbound =
            entry._type === 'transcript' &&
            msg.direction?.toLowerCase() === 'inbound';
          const toAddress = meta?.to?.length
            ? meta.to.join(', ')
            : entry._type === 'transcript' && !isInbound && msg.to
              ? msg.to
              : entry._type === 'reply' && msg.to
                ? msg.to
                : null;
          const ccAddresses: string[] = meta?.cc?.length
            ? meta.cc
            : entry._type === 'reply' && Array.isArray(msg.cc)
              ? msg.cc
              : [];
          const attachmentNames: string[] =
            meta?.attachment_names ??
            (Array.isArray(msg.attachment_names) ? msg.attachment_names : []);

          return (
            <div key={messageKeys[index]} className={styles.row}>
              {/* Gutter with avatar and connecting line */}
              <div className={styles.gutter}>
                {showLineAbove && <div className={styles.lineAbove} />}
                <AvatarCircle tone={avatarTone} userColor={userBadgeColor}>
                  {avatarContent}
                </AvatarCircle>
                {/* Line continues to next entry — hidden on the last message when no compose box follows */}
                {showLineBelow && <div className={styles.lineBelow} />}
              </div>

              {/* Message content */}
              <div
                className={`${styles.content} ${showLineAbove ? styles.contentBelowLine : ''}`}
              >
                <div className={styles.header}>
                  <p
                    className={styles.sender}
                    title={`${senderName} <${senderEmail}>`}
                  >
                    {senderName}{' '}
                    <span
                      className={styles.senderEmail}
                    >{`<${senderEmail}>`}</span>
                  </p>
                  {msg._outbox ? (
                    <OutboxStatus
                      item={msg._outbox}
                      onRetry={composer.retry}
                      onEdit={composer.restore}
                    />
                  ) : (
                    <span
                      className={`email-timestamp cursor-pointer ${styles.timestamp}`}
                      onClick={() => setShowFull((prev) => !prev)}
                    >
                      {showFull
                        ? callEmailTimeFormatted(
                            timestamp,
                            isTimeZone,
                            dateFormat
                          )?.full
                        : callEmailTimeFormatted(
                            timestamp,
                            isTimeZone,
                            dateFormat
                          )?.relative}
                    </span>
                  )}
                </div>

                {toAddress && (
                  <p className={styles.recipients}>
                    {tEmail('to')} {toAddress}
                    {ccAddresses.length > 0 && (
                      <>
                        {' · '}
                        {tEmail('cc')} {ccAddresses.join(', ')}
                      </>
                    )}
                    {/* The subject names what the conversation is about, so only
                        the first email of the chain carries it. */}
                    {index === 0 && meta?.subject && (
                      <>
                        {', '}
                        {tEmail('subject')}: {meta.subject}
                      </>
                    )}
                  </p>
                )}

                {entry._type === 'transcript' ? (
                  // All transcript messages (inbound or outbound) may contain email HTML
                  <p
                    className={styles.body}
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(msg.body),
                    }}
                  />
                ) : (
                  // Human replies sent from this UI are plain text
                  <p className={styles.body}>{msg.body}</p>
                )}

                {attachmentNames.length > 0 && (
                  <div className={styles.attachments}>
                    {uniqueKeys(attachmentNames).map(({ key, value }) => (
                      <div key={key} className={styles.attachment}>
                        <PaperClipOutlined /> {value}
                      </div>
                    ))}
                  </div>
                )}

                <div className={styles.divider} />
              </div>
            </div>
          );
        })}
      </div>

      {showPausedStrip && (
        <PausedStrip
          canResume={Boolean(replyThread?.can_resume_ai)}
          resuming={resuming}
          onResume={handleResumeAi}
        />
      )}

      {composer.visible && (
        <div className={styles.row}>
          <div className={styles.gutter}>
            {allMessages.length > 0 && <div className={styles.lineAbove} />}
            <AvatarCircle tone='user' userColor={userBadgeColor}>
              <span className={`${styles.initials} ${styles.initialsLarge}`}>
                {getInitials(currentUserName)}
              </span>
            </AvatarCircle>
          </div>
          <div
            className={`${styles.composerEditor} ${allMessages.length > 0 ? styles.composerEditorBelowLine : ''}`}
          >
            <EmailComposer composer={composer} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default AiEmailExchange;
